import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getActiveStatusFilterField } from '@/utils/filterFields';
import { promoItemTypesQueryOptions, promoItemsInfiniteQueryOptions } from '../api/promotion.api';
import { getPromoItemColumns } from '../components/PromoColumns';
import { PromoItemFormModal } from '../components/PromoFormModals';
import { LOOKUP_PAGE_SIZE } from '../constants/promotion.constants';
import { usePromoCatalogueFilters } from '../hooks/usePromoCatalogueFilters';
import { useDeletePromoItem } from '../hooks/usePromoMutations';
import type { PromoItem } from '../types/promotion.types';
import { getPromoItemTypeFilterField } from '../utils/promotionFilterFields';
import { getPromoItemStatCards } from '../utils/promotionStatCards';

const LOOKUP_PARAMS = { page: 1, pageSize: LOOKUP_PAGE_SIZE, isActive: true } as const;

export function PromoItemListPage() {
  const controller = usePromoCatalogueFilters('items');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(promoItemsInfiniteQueryOptions(controller.params));
  const { data: types } = useQuery(promoItemTypesQueryOptions(LOOKUP_PARAMS));

  const [editing, setEditing] = useState<PromoItem | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PromoItem | null>(null);

  const remove = useDeletePromoItem(() => setPendingDelete(null));

  const columns = getPromoItemColumns(controller.search.search, {
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
    onDelete: setPendingDelete,
  });

  return (
    <CatalogueListLayout
      title="Promotional Item List"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search by name or code',
      }}
      filters={controller}
      filterFields={[getActiveStatusFilterField(), getPromoItemTypeFilterField(types?.data)]}
      stats={{
        cards: getPromoItemStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Promotional Item"
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the items"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No promotional item matches"
          {...loadMoreProps}
        />
      )}

      <PromoItemFormModal item={editing} open={isFormOpen} onOpenChange={setFormOpen} />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Remove this item?"
        description={
          pendingDelete === null
            ? ''
            : `"${pendingDelete.name}" will no longer be offered. An item that has already been handed out cannot be removed - make it inactive instead.`
        }
        confirmLabel="Remove"
        confirmVariant="destructive"
        isPending={remove.isPending}
        onConfirm={() => pendingDelete && remove.mutate(pendingDelete.id)}
      />
    </CatalogueListLayout>
  );
}
